/**
 * useFollowCamera — integration hook wiring the pure FollowCamera state machine
 * (app/services/map/follow-camera.ts) into a live google.maps.Map instance.
 * Part of the Experimental Premium Live Map Layer (PLA-262 / PLA-266).
 *
 * Responsibilities (the pure reducer owns all mode logic, this hook owns the
 * side effects):
 * - Dispatches `carMoved` on every driver location tick, with
 *   `distanceToDestinationMeters` as the haversine distance to the ACTIVE
 *   destination — the customer pin on the pickup/return legs, the hub pin on the
 *   customer -> hub ("toHub") leg. The caller decides which pin is active and
 *   passes it as `destinationLocation`.
 * - Dispatches `userGesture` from the map's `dragstart` event and from
 *   user-initiated zoom changes. Our own programmatic `setZoom` writes echo back
 *   as `zoom_changed` too, so the handler ignores events whose zoom matches the
 *   last value this hook wrote.
 * - Dispatches `livePhaseChanged` / `configChanged` when the corresponding
 *   options change, and re-dispatches `configChanged` live when the user's
 *   prefers-reduced-motion setting flips.
 * - Applies emitted camera targets to the map: `panTo` + `setZoom` when
 *   `animate` is true, `setCenter` + `setZoom` (jump-cut) when the user prefers
 *   reduced motion. Targets are shifted by half the obstructed bottom band
 *   (`obstructedBottomPx`, e.g. the bottom sheet) so the car centers in the
 *   VISIBLE part of the canvas, and the last target is re-applied when the
 *   band height changes mid-follow (sheet expand/collapse).
 * - Auto-resumes follow after {@link AUTO_RESUME_MS} of gesture inactivity in
 *   `paused` (replaces the former explicit recenter chip).
 *
 * Consumers must let their own bounds-fitting / auto-zoom logic YIELD while
 * `cameraOwned` is true (follow or paused) so the two never fight over the
 * viewport; in overview mode existing behavior is untouched.
 */

import { useCallback, useEffect, useRef, useState } from "react"

import {
  CameraTarget,
  computeCamera,
  createInitialState,
  FollowCameraEvent,
  FollowCameraState,
  offsetLatLngByPixels,
} from "@/services/map/follow-camera"
import { stepTilt } from "@/services/map/map-tilt"
import { prefersReducedMotion, subscribeReducedMotion } from "@/utils/reducedMotion"
import { calculateDistance } from "@/utils/routeHelpers"

interface GeoPoint {
  latitude: number
  longitude: number
}

export interface UseFollowCameraOptions {
  /** Live map instance, or null until the map has loaded. */
  map: google.maps.Map | null
  /** Latest driver location tick (Pusher / dev harness), null when unknown. */
  driverLocation: GeoPoint | null
  /**
   * The active follow destination — the customer pin on the pickup/return legs,
   * the hub pin on the customer -> hub ("toHub") leg. Distance + approach zoom
   * are measured against this.
   */
  destinationLocation: GeoPoint | null
  /** True while the order is in a live-driving phase (en route / return). */
  isLiveDriving: boolean
  /** FleetHub multi-order view — pinned to overview, never follows one car. */
  multiOrder: boolean
  /**
   * Height (px) of UI obstructing the BOTTOM of the map canvas (the bottom
   * sheet). Follow targets center the car in the visible band above it.
   */
  obstructedBottomPx?: number
}

export interface UseFollowCameraResult {
  /**
   * True while the state machine owns the viewport (follow OR paused).
   * Bounds-fitting / auto-zoom logic must yield while this is true.
   */
  cameraOwned: boolean
  /**
   * Latest haversine distance (meters) from the car to the active destination
   * pin — the exact value the camera's approach zoom consumes, recomputed on
   * every driver tick. Null until the first tick. PLA-267 (approach moments)
   * reuses this instead of re-deriving its own distance.
   */
  distanceToDestinationMeters: number | null
  /**
   * True while the map is leaning into 3D (target tilt > 0). Tracks APPLIED tilt,
   * not follow-engagement: it stays true while `paused`, because the user's drag
   * leaves the tilt in place. False far out (flat) and once the camera has
   * flattened back in overview (ADR-0033).
   *
   * NOTE: the comet-tail trail no longer gates on this — it's a tilt-safe vector
   * renderer now (CarTrailOverlay) and stays visible through the 3D view. This
   * value currently has no consumer but is retained because the tilt loop's
   * disengage logic (flatten-ease, Codex P2) is keyed to it; harmless to export.
   */
  tiltEngaged: boolean
}

/** Tolerance when matching a `zoom_changed` echo against our own zoom write. */
const ZOOM_EPSILON = 0.01

/**
 * Gesture-inactivity window in `paused` before follow auto-resumes. After the
 * user pans/zooms away, the camera hands control back to the driver-follow after
 * a full minute of no further gestures (re-centers + re-zooms on the last known
 * car position even if the driver hasn't moved).
 */
const AUTO_RESUME_MS = 60_000

/**
 * Defensive cap on tilt-loop frames per arm (~20 s at 60 fps). The loop already
 * parks when the internal eased value reaches the target; this is belt-and-braces
 * so no pathological input can keep it scheduling forever.
 */
const TILT_MAX_FRAMES = 1200

export function useFollowCamera(options: UseFollowCameraOptions): UseFollowCameraResult {
  const {
    map,
    driverLocation,
    destinationLocation,
    isLiveDriving,
    multiOrder,
    obstructedBottomPx = 0,
  } = options

  // Full machine state lives in a ref (dispatch is called from map event
  // listeners); only the render-relevant slices are mirrored into React state
  // as primitives so unrelated events never cause re-renders.
  const stateRef = useRef<FollowCameraState>(
    createInitialState({ reducedMotion: prefersReducedMotion(), multiOrder }),
  )
  const [mode, setMode] = useState(stateRef.current.mode)
  const [distanceToDestinationMeters, setDistanceToDestinationMeters] = useState<number | null>(
    stateRef.current.distanceToDestinationMeters,
  )
  // True while the ramp has the map leaning into 3D (target tilt > 0). Mirrored
  // into state so the trail overlay can gate on it.
  const [tiltEngaged, setTiltEngaged] = useState(false)

  const mapRef = useRef(map)
  mapRef.current = map
  const reducedMotionRef = useRef(stateRef.current.config.reducedMotion)
  /** Zoom value of OUR last programmatic write — gesture detection ignores it. */
  const lastProgrammaticZoomRef = useRef<number | null>(null)
  const obstructedBottomPxRef = useRef(obstructedBottomPx)
  obstructedBottomPxRef.current = obstructedBottomPx
  /** Last camera target applied (pre-offset) — re-applied when the band changes. */
  const lastTargetRef = useRef<CameraTarget | null>(null)
  const autoResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Stable indirection so the auto-resume timeout always calls the live dispatch. */
  const dispatchRef = useRef<(event: FollowCameraEvent) => void>(() => {})

  // --- Tilt animation (ADR-0033) --------------------------------------------
  // Google Maps JS has no built-in animated tilt, so we ease it ourselves on a
  // rAF loop toward `tiltTargetRef` using the pure `stepTilt`, then park.
  //
  // CRITICAL — the easing is driven from an INTERNAL `tiltCurrentRef`, not from
  // `map.getTilt()`. On a vector map setTilt is silently clamped to the
  // zoom-permitted maximum and getTilt() returns the CLAMPED value; re-reading it
  // every frame would deadlock the loop (we write tilt+1, the map clamps it back,
  // we read the clamp, write tilt+1 again — forever, never reaching `target`).
  // Driving from our own eased value guarantees the loop reaches the target and
  // parks even while the map can only display a smaller tilt at the current zoom.
  // We seed the internal value from the live tilt once per arm so a flatten /
  // resume eases from where the map actually is.
  //
  // Engaged in `follow` (toward the ramped approach tilt) and during the
  // `overview` flatten (toward 0); NOT in `paused`, so a user's tilt is left
  // alone. Per ADR-0033 §5 it animates even under reduced motion (deliberate).
  /** Desired tilt (deg) the rAF loop eases toward. */
  const tiltTargetRef = useRef(0)
  /** Our own eased tilt value — the loop's source of truth (see note above). */
  const tiltCurrentRef = useRef(0)
  /** Active rAF handle for the tilt loop, or null when parked. */
  const tiltRafRef = useRef<number | null>(null)
  /** Timestamp of the previous tilt frame (for frame-rate-independent easing). */
  const tiltLastTsRef = useRef<number | null>(null)
  /** Frames since the loop was armed — defensive cap (see TILT_MAX_FRAMES). */
  const tiltFramesRef = useRef(0)

  const driveTilt = useCallback(() => {
    const liveMap = mapRef.current
    if (!liveMap) {
      tiltRafRef.current = null
      tiltLastTsRef.current = null
      return
    }
    const now = typeof performance !== "undefined" ? performance.now() : Date.now()
    const last = tiltLastTsRef.current
    // Clamp dt so a backgrounded tab (huge gap) doesn't jump the tilt.
    const dt = last === null ? 16 : Math.min(now - last, 100)
    tiltLastTsRef.current = now

    const current = tiltCurrentRef.current
    const target = tiltTargetRef.current
    const next = stepTilt(current, target, dt)
    tiltCurrentRef.current = next
    if (next !== current) liveMap.setTilt?.(next)

    tiltFramesRef.current += 1
    if (next === target || tiltFramesRef.current >= TILT_MAX_FRAMES) {
      // Settled (stepTilt snaps within epsilon) or capped — park until re-armed.
      // Disengage only now that the map has actually reached flat, so the trail
      // stays suppressed for the whole flatten ease (Codex P2). A capped park that
      // is still tilted leaves tiltEngaged true (the map is still leaning).
      if (next === 0) setTiltEngaged(false)
      tiltRafRef.current = null
      tiltLastTsRef.current = null
      return
    }
    tiltRafRef.current = requestAnimationFrame(driveTilt)
  }, [])

  /** Arm the tilt loop if it is parked, seeding the eased value from the map. */
  const ensureTiltLoop = useCallback(() => {
    if (tiltRafRef.current !== null) return
    // Seed from the live tilt so flatten/resume ease from where the map really
    // is. Read ONCE here (not per frame) so the vector clamp can't deadlock us.
    const liveMap = mapRef.current
    const actual = liveMap?.getTilt?.()
    if (typeof actual === "number" && Number.isFinite(actual)) {
      tiltCurrentRef.current = actual
    }
    tiltLastTsRef.current = null
    tiltFramesRef.current = 0
    tiltRafRef.current = requestAnimationFrame(driveTilt)
  }, [driveTilt])

  /** Stop the tilt loop, leaving the map's current tilt untouched. */
  const stopTiltLoop = useCallback(() => {
    if (tiltRafRef.current !== null) {
      cancelAnimationFrame(tiltRafRef.current)
      tiltRafRef.current = null
    }
    tiltLastTsRef.current = null
  }, [])

  const clearAutoResumeTimer = useCallback(() => {
    if (autoResumeTimerRef.current !== null) {
      clearTimeout(autoResumeTimerRef.current)
      autoResumeTimerRef.current = null
    }
  }, [])

  /** Applies a camera target emitted by the reducer to the live map. */
  const applyCamera = useCallback(
    (target: CameraTarget) => {
      const liveMap = mapRef.current
      if (!liveMap) return
      lastTargetRef.current = target
      // Sign check (see offsetLatLngByPixels): +dyPx moves the CENTER toward the
      // bottom of the screen, so the car — the point we keep tracking — renders
      // HIGHER. Shifting by HALF the obstructed band puts the car in the middle
      // of the unobstructed band ABOVE the bottom sheet (band height = canvas -
      // obstructed; band midpoint sits obstructed/2 px above the canvas center).
      const center = offsetLatLngByPixels(
        target.center,
        0,
        obstructedBottomPxRef.current / 2,
        target.zoom,
      )
      const currentZoom = liveMap.getZoom()
      const zoomChanges =
        currentZoom === undefined || Math.abs(currentZoom - target.zoom) > ZOOM_EPSILON
      if (zoomChanges) {
        lastProgrammaticZoomRef.current = target.zoom
      }
      if (target.animate) {
        liveMap.panTo(center)
      } else {
        // Reduced motion: jump-cut, no easing.
        liveMap.setCenter(center)
      }
      if (zoomChanges) {
        liveMap.setZoom(target.zoom)
      }
      // Ease the camera toward the target tilt (ADR-0033). The reducer only emits
      // targets in `follow`, so this runs exactly while following. Re-arm the loop
      // only when the target actually moved — a settled close-in follow keeps
      // emitting the same tilt every tick and must not re-arm on each one.
      const tiltTargetChanged = target.tilt !== tiltTargetRef.current
      tiltTargetRef.current = target.tilt
      // Engage immediately when leaning in (hide the trail before/while tilting).
      // DISENGAGE is deferred to driveTilt, once the map has actually flattened to
      // 0 — flipping it false the instant the target hits 0 would re-show the
      // flat-Mercator trail during the remaining flatten frames while the map is
      // still visibly tilted (Codex P2).
      if (target.tilt > 0) setTiltEngaged(true)
      if (tiltTargetChanged) ensureTiltLoop()
    },
    [ensureTiltLoop],
  )

  const dispatch = useCallback(
    (event: FollowCameraEvent) => {
      const next = computeCamera(stateRef.current, event)
      stateRef.current = next
      if (next.camera) {
        applyCamera(next.camera)
      }
      // Auto-resume: any gesture that lands (or keeps) the machine in `paused`
      // (re)arms the timer; leaving `paused` for any reason disarms it.
      if (event.type === "userGesture" && next.mode === "paused") {
        clearAutoResumeTimer()
        autoResumeTimerRef.current = setTimeout(() => {
          autoResumeTimerRef.current = null
          dispatchRef.current({ type: "resumeFollow" })
        }, AUTO_RESUME_MS)
      } else if (next.mode !== "paused") {
        clearAutoResumeTimer()
      }
      setMode(next.mode)
      setDistanceToDestinationMeters(next.distanceToDestinationMeters)
    },
    [applyCamera, clearAutoResumeTimer],
  )
  dispatchRef.current = dispatch

  // Disarm the auto-resume timer on unmount.
  useEffect(() => clearAutoResumeTimer, [clearAutoResumeTimer])

  // Re-apply the last camera target when the obstructed band height changes
  // while following, so the car glides into the new visible-band center on
  // sheet expand/collapse. Reduced-motion aware: animate is recomputed from
  // the CURRENT preference, not the one captured when the target was emitted.
  useEffect(() => {
    if (stateRef.current.mode !== "follow") return
    const lastTarget = lastTargetRef.current
    if (!lastTarget) return
    applyCamera({ ...lastTarget, animate: !reducedMotionRef.current })
  }, [applyCamera, obstructedBottomPx])

  // configChanged: multiOrder option + live prefers-reduced-motion changes.
  useEffect(() => {
    dispatch({ type: "configChanged", reducedMotion: reducedMotionRef.current, multiOrder })
    return subscribeReducedMotion((matches) => {
      reducedMotionRef.current = matches
      dispatch({ type: "configChanged", reducedMotion: matches, multiOrder })
    })
  }, [dispatch, multiOrder])

  // livePhaseChanged: order status transitions in/out of live driving.
  useEffect(() => {
    dispatch({ type: "livePhaseChanged", isLiveDriving })
  }, [dispatch, isLiveDriving])

  // Tilt driver (ADR-0033): in `follow` the loop eases toward the ramped target
  // applyCamera maintains; in `overview` it flattens to 0; in `paused` it stops
  // so the user's manual tilt is left untouched (re-engages on resume).
  useEffect(() => {
    if (mode === "follow") {
      ensureTiltLoop()
    } else if (mode === "overview") {
      // Flatten to 0; tiltEngaged stays true until driveTilt reaches flat, so the
      // trail is suppressed for the whole flatten ease (Codex P2).
      tiltTargetRef.current = 0
      ensureTiltLoop()
    } else {
      stopTiltLoop()
    }
  }, [mode, ensureTiltLoop, stopTiltLoop])

  // Stop the tilt loop on unmount.
  useEffect(() => stopTiltLoop, [stopTiltLoop])

  // carMoved: one event per driver location tick. Dispatched in every mode so
  // the machine's last-known car position stays warm for an instant recenter.
  const driverLat = driverLocation?.latitude
  const driverLng = driverLocation?.longitude
  const destinationLat = destinationLocation?.latitude
  const destinationLng = destinationLocation?.longitude
  useEffect(() => {
    if (
      driverLat === undefined ||
      driverLng === undefined ||
      destinationLat === undefined ||
      destinationLng === undefined
    ) {
      return
    }
    if (
      !Number.isFinite(driverLat) ||
      !Number.isFinite(driverLng) ||
      !Number.isFinite(destinationLat) ||
      !Number.isFinite(destinationLng)
    ) {
      return
    }
    dispatch({
      type: "carMoved",
      position: { lat: driverLat, lng: driverLng },
      distanceToDestinationMeters: calculateDistance(
        { latitude: driverLat, longitude: driverLng },
        { latitude: destinationLat, longitude: destinationLng },
      ),
    })
  }, [dispatch, driverLat, driverLng, destinationLat, destinationLng])

  // userGesture: drags always count; zoom changes only when user-initiated
  // (our own setZoom writes echo back as zoom_changed and must be ignored).
  useEffect(() => {
    if (!map) return undefined
    const dragListener = map.addListener("dragstart", () => {
      dispatch({ type: "userGesture" })
    })
    const zoomListener = map.addListener("zoom_changed", () => {
      const zoom = map.getZoom()
      const expected = lastProgrammaticZoomRef.current
      if (zoom !== undefined && expected !== null && Math.abs(zoom - expected) <= ZOOM_EPSILON) {
        return // our own programmatic write echoing back — not a user gesture
      }
      dispatch({ type: "userGesture" })
    })
    return () => {
      dragListener.remove()
      zoomListener.remove()
    }
  }, [map, dispatch])

  return {
    cameraOwned: mode !== "overview",
    distanceToDestinationMeters,
    tiltEngaged,
  }
}
